import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useRBAC } from '../contexts/RBACContext';

/**
 * Hook for Signal Engine data: profile + scored_signals + admin actions.
 * Follows the same pattern as useCallListDB.
 */
export function useSignalData({
  statusFilter = 'all',
  reviewFilter = 'all',
  sortBy = 'relevance_score',
  sortOrder = 'desc',
  page = 1,
  pageSize = 10,
  adminProfileId = null,
  countryFilter = '',
  eventTypeFilter = '',
  minScoreFilter = 0,
  originFilter = '',
} = {}) {
  const { currentUser } = useAuth();
  const { isAdminOrHigher, hasPermission } = useRBAC();

  const [profile, setProfile] = useState(null);
  const [allProfiles, setAllProfiles] = useState([]);
  // Every profile this user may view, admin or not. Non-admins can legitimately
  // be granted more than one, and previously had no way to reach the others.
  const [accessibleProfiles, setAccessibleProfiles] = useState([]);
  const [signals, setSignals] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [signalsLoading, setSignalsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ countries: [], eventTypes: [] });

  const reqId = useRef(0);

  // ─── Resolve the active profile ───
  // One path for everyone: load the accessible set, expose it so the UI can
  // offer a switcher, then select the requested profile or the first one.
  // Selection is validated against the accessible set, so passing an id the
  // user has no membership in cannot widen their access — only an admin falls
  // through to the direct query.
  const fetchProfile = useCallback(async () => {
    if (!currentUser?.email) return;
    try {
      const { data: profiles, error: err } = await supabase
        .rpc('get_user_profiles', { p_user_email: currentUser.email.toLowerCase() });

      if (err) throw err;

      const rows = profiles || [];
      const mapped = rows.map(p => ({
        id: p.profile_id,
        org_name: p.org_name,
        org_industry: p.org_industry,
        is_active: p.is_active,
        auto_approve: p.auto_approve,
        approved_count: p.approved_count,
        user_role: p.user_role,
        can_edit_profile: p.can_edit_profile,
        can_approve_signals: p.can_approve_signals,
        is_primary_owner: p.is_primary_owner,
      }));
      setAccessibleProfiles(mapped);

      const requested = adminProfileId ? mapped.find(p => p.id === adminProfileId) : null;
      if (requested) {
        setProfile(requested);
        return;
      }

      // Admins may inspect a profile they are not a member of; get_user_profiles
      // would not return it, so read it directly.
      if (adminProfileId && isAdminOrHigher()) {
        const { data, error: directErr } = await supabase
          .from('signal_profiles')
          .select('*')
          .eq('id', adminProfileId)
          .single();
        if (directErr) throw directErr;
        setProfile(data);
        return;
      }

      setProfile(mapped.length > 0 ? mapped[0] : null);
    } catch (err) {
      console.error('fetchProfile error:', err);
      setError(err.message);
    }
  }, [currentUser?.email, adminProfileId, isAdminOrHigher]);

  // ─── Fetch all profiles (admin only) ───
  const fetchAllProfiles = useCallback(async () => {
    if (!currentUser?.email || !isAdminOrHigher()) return;
    try {
      const { data, error: err } = await supabase
        .from('signal_profiles')
        .select('*')
        .order('created_at', { ascending: false });
      if (err) throw err;
      setAllProfiles(data || []);
    } catch (err) {
      console.error('fetchAllProfiles error:', err);
    }
  }, [currentUser?.email, isAdminOrHigher]);

  // ─── Fetch scored signals for the active profile ───
  const fetchSignals = useCallback(async () => {
    const activeProfileId = adminProfileId || profile?.id;
    if (!activeProfileId) {
      setSignals([]);
      setTotal(0);
      setTotalPages(0);
      return;
    }

    const thisReq = ++reqId.current;
    setSignalsLoading(true);

    try {
      let query = supabase
        .from('scored_signals')
        .select('*, scored_signal_sources(id, raw_signal_id, event_type, contribution_note, added_at, raw_signals(source, category))', { count: 'exact' })
        .eq('profile_id', activeProfileId);

      // Status filter
      if (statusFilter && statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      // Review filter - CRITICAL FIX
      if (reviewFilter === 'pending_review') {
        // Admin viewing Review Queue - show pending
        query = query.eq('review_status', 'pending_review');
      } else if (reviewFilter === 'approved') {
        // Admin viewing approved only
        query = query.eq('review_status', 'approved');
      } else if (reviewFilter === 'rejected') {
        // Admin viewing rejected
        query = query.eq('review_status', 'rejected');
      } else {
        // Default: Regular users and admins viewing "New/Reviewed/Dismissed" tabs
        // MUST only show approved signals
        query = query.eq('review_status', 'approved');
      }

      // Server-side filters
      if (countryFilter) {
        query = query.eq('country', countryFilter);
      }
      if (eventTypeFilter) {
        query = query.contains('event_types', [eventTypeFilter]);
      }
      if (minScoreFilter > 0) {
        query = query.gte('relevance_score', minScoreFilter);
      }
      if (originFilter) {
        query = query.eq('origin', originFilter);
      }

      // Sorting
      const validSortColumns = ['relevance_score', 'created_at', 'company', 'confidence', 'source_count', 'updated_at'];
      const col = validSortColumns.includes(sortBy) ? sortBy : 'relevance_score';
      query = query.order(col, { ascending: sortOrder === 'asc' });

      // Pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data, error: err, count } = await query;

      if (thisReq !== reqId.current) return;
      if (err) throw err;

      setSignals(data || []);
      setTotal(count || 0);
      setTotalPages(Math.ceil((count || 0) / pageSize));
    } catch (err) {
      if (thisReq === reqId.current) setError(err.message);
    } finally {
      if (thisReq === reqId.current) setSignalsLoading(false);
    }
  }, [profile?.id, adminProfileId, statusFilter, reviewFilter, sortBy, sortOrder, page, pageSize, countryFilter, eventTypeFilter, minScoreFilter, originFilter]);

  // ─── Fetch distinct filter options (with counts) for the ACTIVE scope ───
  // Previously hardcoded to review_status='pending_review', so the All
  // Signals tab had no filter options to offer.
  const fetchFilterOptions = useCallback(async () => {
    const activeProfileId = adminProfileId || profile?.id;
    if (!activeProfileId) return;

    try {
      let optQuery = supabase
        .from('scored_signals')
        .select('country, event_types, origin')
        .eq('profile_id', activeProfileId);

      if (reviewFilter === 'pending_review' || reviewFilter === 'rejected') {
        optQuery = optQuery.eq('review_status', reviewFilter);
      } else {
        optQuery = optQuery.eq('review_status', 'approved');
      }

      if (originFilter) {
        optQuery = optQuery.eq('origin', originFilter);
      }

      const { data: allData } = await optQuery;

      // Build country counts: [{ code: 'FI', count: 45 }, ...]
      const countryCounts = {};
      (allData || []).forEach(r => {
        if (r.country) {
          countryCounts[r.country] = (countryCounts[r.country] || 0) + 1;
        }
      });
      const countries = Object.entries(countryCounts)
        .map(([code, count]) => ({ code, count }))
        .sort((a, b) => a.code.localeCompare(b.code));

      // Build event type counts: [{ type: 'expansion', count: 30 }, ...]
      const eventCounts = {};
      (allData || []).forEach(r => {
        (r.event_types || []).forEach(t => {
          eventCounts[t] = (eventCounts[t] || 0) + 1;
        });
      });
      const eventTypes = Object.entries(eventCounts)
        .map(([type, count]) => ({ type, count }))
        .sort((a, b) => a.type.localeCompare(b.type));

      // Build origin counts: [{ origin: 'country_pack', count: 100 }, ...]
      // Use the full dataset (before originFilter) so both pills always show.
      const originCounts = {};
      (allData || []).forEach(r => {
        const o = r.origin || 'google_news';
        originCounts[o] = (originCounts[o] || 0) + 1;
      });
      const origins = Object.entries(originCounts)
        .map(([origin, count]) => ({ origin, count }))
        .sort((a, b) => a.origin.localeCompare(b.origin));

      setFilterOptions({ countries, eventTypes, origins });
    } catch (err) {
      console.error('fetchFilterOptions error:', err);
    }
  }, [profile?.id, adminProfileId, reviewFilter, originFilter]);

  // ─── Fetch ALL signal IDs matching current filters (for select-all across pages) ───
  const fetchAllMatchingIds = useCallback(async () => {
    const activeProfileId = adminProfileId || profile?.id;
    if (!activeProfileId) return [];

    try {
      let query = supabase
        .from('scored_signals')
        .select('id')
        .eq('profile_id', activeProfileId)
        .eq('review_status', 'pending_review');

      if (countryFilter) query = query.eq('country', countryFilter);
      if (eventTypeFilter) query = query.contains('event_types', [eventTypeFilter]);
      if (minScoreFilter > 0) query = query.gte('relevance_score', minScoreFilter);
      if (originFilter) query = query.eq('origin', originFilter);

      const { data, error: err } = await query;
      if (err) throw err;
      return (data || []).map(r => r.id);
    } catch (err) {
      console.error('fetchAllMatchingIds error:', err);
      return [];
    }
  }, [profile?.id, adminProfileId, countryFilter, eventTypeFilter, minScoreFilter, originFilter]);

  // ─── Create profile ───
  const createProfile = useCallback(async (profileData) => {
    if (!currentUser?.email) throw new Error('Not authenticated');
    try {
      // Admin can create profiles for other users by specifying user_email
      // Non-admins always use their own email
      const targetEmail = (isAdminOrHigher() && profileData.user_email)
        ? profileData.user_email.toLowerCase().trim()
        : currentUser.email.toLowerCase();

      const insertPayload = {
          user_email: targetEmail,
          org_name: profileData.org_name.trim(),
          org_description: profileData.org_description?.trim() || null,
          org_industry: profileData.org_industry.trim(),
          org_website: profileData.org_website?.trim() || null,
          target_config: profileData.target_config,
        };
      if (profileData.run_interval_days != null) {
        insertPayload.run_interval_days = profileData.run_interval_days;
      }
      if (profileData.max_daily_runs != null) {
        insertPayload.max_daily_runs = profileData.max_daily_runs;
      }

      const { data, error: err } = await supabase
        .from('signal_profiles')
        .insert(insertPayload)
        .select()
        .single();

      if (err) {
        // Provide user-friendly error for UNIQUE constraint violation
        if (err.code === '23505' && err.message?.includes('user_email')) {
          throw new Error(`A profile already exists for ${targetEmail}. Each user can only have one profile.`);
        }
        throw err;
      }
      setProfile(data);

      // Refresh admin profile list
      if (isAdminOrHigher()) await fetchAllProfiles();

      // Fire webhook for immediate first run (non-blocking)
      const webhookUrl = process.env.REACT_APP_SIGNAL_DISPATCHER_WEBHOOK;
      const webhookSecret = process.env.REACT_APP_SIGNAL_WEBHOOK_SECRET;
      if (webhookUrl && webhookSecret) {
        fetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Signal-Secret': webhookSecret,
          },
          body: JSON.stringify({ profile_id: data.id }),
        }).catch(triggerErr => console.error('Webhook trigger failed (non-blocking):', triggerErr));
      }

      return data;
    } catch (err) {
      console.error('createProfile error:', err);
      throw err;
    }
  }, [currentUser?.email, isAdminOrHigher, fetchAllProfiles]);

  // ─── Update profile ───
  const updateProfile = useCallback(async (updates) => {
    const targetId = adminProfileId || profile?.id;
    if (!targetId) throw new Error('No active profile');
    try {
      // Use RPC to update profile (bypasses RLS, checks permissions internally)
      const { data: result, error: rpcErr } = await supabase.rpc('update_signal_profile', {
        p_profile_id: targetId,
        p_user_email: currentUser?.email?.toLowerCase(),
        p_updates: updates
      });

      if (rpcErr) throw rpcErr;
      if (result?.error) throw new Error(result.error);

      // run_interval_days and max_daily_runs are not handled by the RPC — update directly
      const directUpdates = {};
      if (updates.run_interval_days != null) directUpdates.run_interval_days = updates.run_interval_days;
      if (updates.max_daily_runs != null) directUpdates.max_daily_runs = updates.max_daily_runs;
      if (Object.keys(directUpdates).length > 0) {
        const { error: directErr } = await supabase
          .from('signal_profiles')
          .update(directUpdates)
          .eq('id', targetId);
        if (directErr) throw directErr;
      }

      // Refresh profile data
      await fetchProfile();
      if (isAdminOrHigher()) await fetchAllProfiles();
      return result;
    } catch (err) {
      console.error('updateProfile error:', err);
      throw err;
    }
  }, [profile?.id, adminProfileId, currentUser?.email, isAdminOrHigher, fetchProfile, fetchAllProfiles]);

  // ─── Toggle pause/resume ───
  const toggleActive = useCallback(async () => {
    const targetId = adminProfileId || profile?.id;
    if (!targetId) return;
    
    // Determine current state
    const currentProfile = adminProfileId 
      ? allProfiles.find(p => p.id === adminProfileId) 
      : profile;
    
    if (!currentProfile) return;
    
    try {
      // Use RPC to toggle active status (bypasses RLS, checks permissions internally)
      const { data: result, error: rpcErr } = await supabase.rpc('toggle_profile_active', {
        p_profile_id: targetId,
        p_user_email: currentUser?.email?.toLowerCase()
      });

      if (rpcErr) throw rpcErr;
      
      if (result && result.error) {
        throw new Error(result.error);
      }

      const newIsActive = result?.is_active ?? !currentProfile.is_active;
      const updatedProfile = { ...currentProfile, is_active: newIsActive };
      
      // Update the correct state
      if (adminProfileId) {
        setAllProfiles(prev => prev.map(p => p.id === targetId ? { ...p, is_active: newIsActive } : p));
      }
      setProfile(updatedProfile);
      return updatedProfile;
    } catch (err) {
      console.error('toggleActive error:', err);
      throw err;
    }
  }, [profile?.id, profile?.is_active, adminProfileId, allProfiles, currentUser?.email, isAdminOrHigher]);

  // ─── Update signal status (new → reviewed/dismissed) ───
  const updateSignalStatus = useCallback(async (signalId, newStatus) => {
    try {
      const { error: err } = await supabase
        .from('scored_signals')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', signalId);

      if (err) throw err;
      setSignals(prev => prev.map(s => s.id === signalId ? { ...s, status: newStatus } : s));
    } catch (err) {
      console.error('updateSignalStatus error:', err);
      throw err;
    }
  }, []);

  // ─── Admin: approve/reject signal (uses atomic RPC) ───
  const reviewSignal = useCallback(async (signalId, action) => {
    if (!isAdminOrHigher()) throw new Error('Admin only');
    const decision = action === 'approve' ? 'approved' : 'rejected';

    try {
      const { data: result, error: err } = await supabase.rpc('admin_review_signal', {
        p_scored_signal_id: signalId,
        p_admin_email: currentUser.email.toLowerCase(),
        p_decision: decision,
      });

      if (err) throw err;
      if (result && result.error) throw new Error(result.error);

      // Update local state
      setSignals(prev => prev.map(s =>
        s.id === signalId
          ? { ...s, review_status: decision, reviewed_by: currentUser.email, reviewed_at: new Date().toISOString() }
          : s
      ));

      // Refresh profile to get updated counts
      await fetchProfile();
      if (isAdminOrHigher()) await fetchAllProfiles();
    } catch (err) {
      console.error('reviewSignal error:', err);
      throw err;
    }
  }, [isAdminOrHigher, currentUser?.email, fetchProfile, fetchAllProfiles]);

  // ─── Admin: batch approve/reject multiple signals ───
  const batchReviewSignals = useCallback(async (signalIds, action) => {
    if (!isAdminOrHigher()) throw new Error('Admin only');
    if (!signalIds || signalIds.length === 0) throw new Error('No signals selected');
    const decision = action === 'approve' ? 'approved' : 'rejected';

    try {
      const { data: result, error: err } = await supabase.rpc('admin_batch_review_signals', {
        p_signal_ids: signalIds,
        p_admin_email: currentUser.email.toLowerCase(),
        p_decision: decision,
      });

      if (err) throw err;
      if (result?.error) throw new Error(result.error);

      // Update local state for all processed signals
      setSignals(prev => prev.map(s =>
        signalIds.includes(s.id)
          ? { ...s, review_status: decision, reviewed_by: currentUser.email, reviewed_at: new Date().toISOString() }
          : s
      ));

      // Refresh profile to get updated counts
      await fetchProfile();
      if (isAdminOrHigher()) await fetchAllProfiles();

      return result;
    } catch (err) {
      console.error('batchReviewSignals error:', err);
      throw err;
    }
  }, [isAdminOrHigher, currentUser?.email, fetchProfile, fetchAllProfiles]);

  // ─── Mark delivered ───
  const markDelivered = useCallback(async (signalId) => {
    try {
      await supabase
        .from('scored_signals')
        .update({ delivered_at: new Date().toISOString() })
        .eq('id', signalId)
        .is('delivered_at', null);
    } catch (err) {
      console.error('markDelivered error:', err);
    }
  }, []);

  // ─── Initial load ───
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchProfile();
      if (isAdminOrHigher()) {
        await fetchAllProfiles();
      }
      setLoading(false);
    };
    init();
  }, [fetchProfile, fetchAllProfiles, isAdminOrHigher]);

  // ─── Fetch signals when profile or filters change ───
  useEffect(() => {
    fetchSignals();
  }, [fetchSignals]);

  // ─── Fetch filter options when profile or scope changes ───
  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  // ─── Real-time subscription ───
  useEffect(() => {
    if (!currentUser?.email) return;

    const channel = supabase
      .channel('signals-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'scored_signals' }, () => fetchSignals())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'signal_profiles' }, () => {
        fetchProfile();
        if (isAdminOrHigher()) fetchAllProfiles();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.email]);

  // ─── Load profile target_config (may not be in RPC result) ───
  const loadProfileTargetConfig = useCallback(async (profileId) => {
    if (!profileId) return null;
    try {
      const { data, error: err } = await supabase
        .from('signal_profiles')
        .select('target_config')
        .eq('id', profileId)
        .maybeSingle();
      if (err) throw err;
      return data?.target_config || null;
    } catch (err) {
      console.error('loadProfileTargetConfig error:', err);
      return null;
    }
  }, []);

  // ─── Intelligence Overview data ───
  const loadIntelligenceOverview = useCallback(async (profileId) => {
    if (!profileId) return null;
    // The RPC reports authorization failures inside the payload rather than
    // as a transport error, so an unchecked call would render an empty
    // dashboard that looks like "no data" instead of "no access".
    const { data, error: err } = await supabase.rpc('get_intelligence_overview', { p_profile_id: profileId });
    if (err) throw err;
    if (data && data.error) throw new Error(data.error);
    return data;
  }, []);

  // ─── Load country source registry ───
  const loadSourceRegistry = useCallback(async () => {
    try {
      const { data, error: err } = await supabase
        .from('country_source_registry')
        .select('*')
        .order('country_code', { ascending: true });
      if (err) throw err;
      return data || [];
    } catch (err) {
      console.error('loadSourceRegistry error:', err);
      return [];
    }
  }, []);

  // ─── Load latest intelligence report ───
  const loadLatestReport = useCallback(async (profileId) => {
    if (!profileId) return null;
    try {
      const { data, error: err } = await supabase
        .from('signal_intelligence_reports')
        .select('*')
        .eq('profile_id', profileId)
        .order('generated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (err) throw err;
      return data;
    } catch (err) {
      console.error('loadLatestReport error:', err);
      return null;
    }
  }, []);

  // ─── Load report history (all weeks) ───
  const loadReportHistory = useCallback(async (profileId) => {
    if (!profileId) return [];
    try {
      const { data, error: err } = await supabase
        .from('signal_intelligence_reports')
        .select('id,profile_id,report_period,report_type,ai_insight,signals_analysed,generated_at,model_used,tokens_used,report_sections,detected_patterns,source_summary,high_conviction_signals')
        .eq('profile_id', profileId)
        .order('generated_at', { ascending: false })
        .limit(52);
      if (err) throw err;
      return data || [];
    } catch (err) {
      console.error('loadReportHistory error:', err);
      return [];
    }
  }, []);

  // ─── Admin: Update source registry entry ───
  const updateSourceRegistryEntry = useCallback(async (id, updates) => {
    if (!isAdminOrHigher()) throw new Error('Admin only');
    try {
      const { data, error: err } = await supabase
        .from('country_source_registry')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (err) throw err;
      return data;
    } catch (err) {
      console.error('updateSourceRegistryEntry error:', err);
      throw err;
    }
  }, [isAdminOrHigher]);

  // ─── Companies: Load company spine ───
  const loadCompanies = useCallback(async (profileId) => {
    try {
      const targetProfile = profileId || profile?.id;
      if (!targetProfile) return [];

      // Get target countries from profile
      const { data: profileData } = await supabase
        .from('signal_profiles')
        .select('target_config')
        .eq('id', targetProfile)
        .single();

      const tc = profileData?.target_config || {};
      const pack = tc.country_pack || {};
      const countries = pack.intelligence_countries?.length > 0
        ? pack.intelligence_countries
        : tc.target_geographies || [];

      if (countries.length === 0) return [];

      // Collect PKD codes from profile config so we only show companies
      // whose industry_codes overlap with what this profile cares about.
      const pkdCodes = (pack.pkd_codes || [])
        .map(c => String(c).trim())
        .filter(Boolean);

      let query = supabase
        .from('companies')
        .select('*')
        .in('country_code', countries)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(200);

      // NOTE: Not filtering by PKD codes here because auto-created companies
      // (from grants and scored signals) don't have industry_codes populated.
      // PKD filtering can be done client-side in the CompanySpine component
      // once companies have been enriched with registry data.

      const { data, error: err } = await query;

      if (err) throw err;
      const companies = data || [];
      if (companies.length === 0) return companies;

      // Fetch match counts for these companies so the UI can show
      // "With Signals" and "With Grants" stats without loading each
      // corroboration view separately.
      const companyIds = companies.map(c => c.id);
      const { data: matches, error: matchErr } = await supabase
        .from('company_matches')
        .select('company_id, signal_layer')
        .eq('profile_id', targetProfile)
        .in('company_id', companyIds);

      if (matchErr) {
        console.error('loadCompanies match count error:', matchErr);
        return companies;
      }

      const counts = {};
      (matches || []).forEach(m => {
        if (!counts[m.company_id]) counts[m.company_id] = { signal_count: 0, grant_count: 0 };
        counts[m.company_id].signal_count++;
        if (m.signal_layer === 'layer2_grant' || m.signal_layer === 'layer2_permit') {
          counts[m.company_id].grant_count++;
        }
      });

      return companies.map(c => ({
        ...c,
        signal_count: counts[c.id]?.signal_count || 0,
        grant_count: counts[c.id]?.grant_count || 0,
      }));
    } catch (err) {
      console.error('loadCompanies error:', err);
      return [];
    }
  }, [profile?.id]);

  // ─── Companies: Load corroboration data for a single company ───
  const loadCompanyCorroboration = useCallback(async (companyId, profileId) => {
    try {
      const targetProfile = profileId || profile?.id;
      if (!targetProfile || !companyId) return null;

      const { data, error: err } = await supabase
        .rpc('get_company_signal_corroboration', {
          p_company_id: companyId,
          p_profile_id: targetProfile
        });

      if (err) throw err;
      return data;
    } catch (err) {
      console.error('loadCompanyCorroboration error:', err);
      return null;
    }
  }, [profile?.id]);

  // ─── Companies: Load grant sources (scoped to profile's countries) ───
  const loadGrantSources = useCallback(async (profileId) => {
    try {
      const targetProfile = profileId || profile?.id;

      // Determine which countries this profile covers
      let countries = [];
      if (targetProfile) {
        const { data: pd } = await supabase
          .from('signal_profiles')
          .select('target_config')
          .eq('id', targetProfile)
          .single();
        const pack = pd?.target_config?.country_pack || {};
        countries = pack.intelligence_countries?.length > 0
          ? pack.intelligence_countries
          : pd?.target_config?.target_geographies || [];
      }

      let query = supabase
        .from('grant_sources')
        .select('*')
        .eq('is_enabled', true)
        .order('country_code', { ascending: true });

      if (countries.length > 0) {
        query = query.in('country_code', countries);
      }

      const { data, error: err } = await query;
      if (err) throw err;
      return data || [];
    } catch (err) {
      console.error('loadGrantSources error:', err);
      return [];
    }
  }, [profile?.id]);

  return {
    // Profile
    profile,
    allProfiles,
    accessibleProfiles,
    loading,
    error,
    // Signals
    signals,
    signalsLoading,
    total,
    totalPages,
    filterOptions,
    // Actions
    createProfile,
    updateProfile,
    toggleActive,
    updateSignalStatus,
    reviewSignal,
    batchReviewSignals,
    markDelivered,
    fetchAllMatchingIds,
    refetchProfile: fetchProfile,
    refetchSignals: fetchSignals,
    // Intelligence
    loadIntelligenceOverview,
    loadSourceRegistry,
    loadLatestReport,
    loadReportHistory,
    updateSourceRegistryEntry,
    loadProfileTargetConfig,
    // Companies
    loadCompanies,
    loadCompanyCorroboration,
    loadGrantSources,
  };
}
